import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostForm, apiPostJson } from './client'

/**
 * Panoul din dreapta listei Inventoree :: Live Devices, cand sunt bifate randuri.
 *
 * In portal: `MultipleRowsSelect` ridica `multiRowsAllChecked`, iar
 * `DiscoveryMultiActions` (public/js/discovery.js) incarca
 * `discovery/multiActionsTabs`; fiecare fila e alt cap care intoarce HTML.
 * Aici filele intorc date; markupul e al ecranului.
 */

export type DiscoveredTabKey = 'precheck' | 'deploy' | 'remove-agent' | 'actions'

export interface DiscoveredTab {
  key: DiscoveredTabKey
  label: string
  icon: string
}

export interface DiscoveredTabs {
  tabs: DiscoveredTab[]
}

/**
 * Un rand din Pass Safe al firmei, FARA parola - `Discovery::passSafeCredentials`.
 * In coada intra doar `id`; contul il pune API-ul cand da comanda agentului.
 */
export interface DiscoveredCredential {
  id: number
  user: string
  description: string
  device_name: string
}

export interface DiscoveredCredentials {
  credentials: DiscoveredCredential[]
}

export interface DiscoveredSent {
  ok: number
  devices: number
  /** Cate au apucat sa plece spre un releu. */
  sent?: number
  /** Cate n-au niciun releu prin care sa fie strigate. */
  noRelay?: number
}

const BASE = '/inventoree/discovered/multi-actions'

function devicesBody(ids: number[]): FormData {
  const body = new FormData()
  for (const id of ids) body.append('devices[]', String(id))
  return body
}

export const discoveredTabsQuery = queryOptions({
  queryKey: ['inventoree', 'discovered', 'multi-actions', 'tabs'],
  queryFn: () => apiGet<DiscoveredTabs>(`${BASE}/tabs`),
  staleTime: 5 * 60 * 1000,
})

export const discoveredCredentialsQuery = queryOptions({
  queryKey: ['inventoree', 'discovered', 'multi-actions', 'credentials'],
  queryFn: () => apiGet<DiscoveredCredentials>(`${BASE}/credentials`),
  staleTime: 60 * 1000,
})

/** `credential_id`, ca in `_credential_picker.php`: numarul randului, niciodata parola. */
function withCredential(ids: number[], credentialId: number): FormData {
  const body = devicesBody(ids)
  body.set('credential_id', String(credentialId))
  return body
}

export function deployAgent(ids: number[], credentialId: number) {
  return apiPostForm<DiscoveredSent>(`${BASE}/deploy`, withCredential(ids, credentialId))
}

/** `discovery/run_multiple_precheck`: TOD, proba dinaintea instalarii. */
export function precheckAgent(ids: number[], credentialId: number) {
  return apiPostForm<DiscoveredSent>(`${BASE}/precheck`, withCredential(ids, credentialId))
}

export function removeAgent(ids: number[], credentialId: number) {
  return apiPostForm<DiscoveredSent>(`${BASE}/remove-agent`, withCredential(ids, credentialId))
}

export function checkDiscoveredAlive(ids: number[]) {
  return apiPostForm<DiscoveredSent>(`${BASE}/check-alive`, devicesBody(ids))
}

export function removeDiscovered(ids: number[]) {
  return apiPostForm<DiscoveredSent>(`${BASE}/remove`, devicesBody(ids))
}

/*
 * Actiunile de pe UN rand si cele de pe toata lista - `Discovery::deployOne`,
 * `precheckOne`, `moveToStatic`, `moveManyToStatic`, `presence`,
 * `updateDiscovered`, `clear`, `clearRelay`, `identifyRelay`,
 * `duplicatesPreview` / `duplicatesConsolidate`, `last_events`.
 * Raspunsul poarta `status` OK / ERROR si mesajul portalului.
 */

const ROW = '/inventoree/discovered'

export interface DiscoveredStatus {
  status: 'OK' | 'ERROR'
  message?: string
}

export interface DiscoveredPromoted extends DiscoveredStatus {
  id?: number
}

export interface DiscoveredPromotedMany extends DiscoveredStatus {
  done?: number
  failed?: number
}

export interface DiscoveredPresenceRun {
  run_id: number
  seen_at: string | null
  finished: string | null
  state: string
  hostname: string | null
  ip: string | null
  mac: string | null
  matched_by: string
}

export interface DiscoveredPresence extends DiscoveredStatus {
  timeline?: DiscoveredPresenceRun[]
}

/** Releele luate in calcul la "Consolidate duplicates". */
export type ConsolidateScope = 'relay' | 'office' | 'company'

/** Optiunile ferestrei; aceleasi pleaca si la previzualizare, si la aplicare. */
export interface ConsolidateOptions {
  scope: ConsolidateScope
  into_mac: boolean
  same_name: boolean
  /** 0 = toate sediile */
  office: number
  /** 0 = fara curatenie pe vechime */
  stale_days: number
}

export const CONSOLIDATE_DEFAULTS: ConsolidateOptions = { scope: 'office', into_mac: true, same_name: false, office: 0, stale_days: 0 }

/** Un rand din previzualizare (`DuplicatePlanner::view`). */
export interface DuplicateRow {
  id: number
  ip: number
  address: string
  mac: string
  name: string
  relay: number
  relay_name: string
  office: number
  first_saw: string | null
  last_saw: string | null
  uuid: string
  referenced: boolean
}

export interface DuplicateGroup {
  survivor: DuplicateRow
  remove: DuplicateRow[]
  /** Al doilea rand referit de CI / triere: ramane si el. */
  kept: DuplicateRow[]
  merged: { first_saw: string | null; last_saw: string | null; device_name: string; mac: string; uuid: string }
}

export interface DiscoveredDuplicates extends DiscoveredStatus {
  groups: number
  removable: number
  removed: number
  stale?: number
  stale_removed?: number
  ambiguous?: number
  skipped?: number
  /** Randuri referite de CI / triere pe care omul le pusese la unit: lasate neatinse. */
  skipped_rows?: number
  /** Grupuri in care randul referit a ramas principal in locul celui ales de om. */
  linked_main?: number
  skipped_detail?: { survivor: number; reason: string; rows?: number[] }[]
  plan?: DuplicateGroup[]
  stale_rows?: DuplicateRow[]
  options?: ConsolidateOptions
  offices?: { id: number; name: string }[]
  /** "Consolidate automatically after each scan" pe firma. */
  auto?: boolean
  /** Numai Super Admin aplica; ceilalti vad doar previzualizarea. */
  can_apply?: boolean
}

export interface DiscoveredEvent {
  ID: number
  event_log: string
  event_date: string
  user: string
}

/** Formularul "Add as unmanaged device" (`_static_asset_modal.php`). */
export interface PromoteFields {
  device_name: string
  device_type: string
  device_brand: string
  device_office: string
}

export function deployOne(id: number, credentialId: number) {
  return apiPost<DiscoveredStatus>(`${ROW}/${id}/deploy`, { credential_id: credentialId })
}

export function precheckOne(id: number, credentialId: number) {
  return apiPost<DiscoveredStatus>(`${ROW}/${id}/precheck`, { credential_id: credentialId })
}

export function checkOneAlive(id: number) {
  return apiPost<DiscoveredStatus & DiscoveredSent>(`${ROW}/${id}/check-alive`, {})
}

export function removeOne(id: number) {
  return apiPost<DiscoveredStatus & DiscoveredSent>(`${ROW}/${id}/remove`, {})
}

export function promoteDiscovered(id: number, fields: PromoteFields) {
  return apiPost<DiscoveredPromoted>(`${ROW}/${id}/promote`, { ...fields })
}

export function promoteManyDiscovered(ids: number[], fields: Omit<PromoteFields, 'device_name'>) {
  const body = devicesBody(ids)
  for (const [k, v] of Object.entries(fields)) body.set(k, v)
  return apiPostForm<DiscoveredPromotedMany>(`${ROW}/promote-many`, body)
}

export function discoveredPresenceQuery(id: number) {
  return queryOptions({
    queryKey: ['inventoree', 'discovered', 'presence', id],
    queryFn: () => apiGet<DiscoveredPresence>(`${ROW}/${id}/presence`),
    staleTime: 60 * 1000,
  })
}

export function classifyDiscovered(id: number, fields: { type_id?: number; manufacturer_id?: number }) {
  return apiPost<DiscoveredStatus>(`${ROW}/${id}/classification`, { ...fields })
}

function consolidateQuery(o: ConsolidateOptions): string {
  const q = new URLSearchParams({
    scope: o.scope,
    into_mac: o.into_mac ? '1' : '0',
    same_name: o.same_name ? '1' : '0',
    office: String(o.office),
    stale_days: String(o.stale_days),
  })
  return q.toString()
}

export function discoveredDuplicatesQuery(o: ConsolidateOptions) {
  return queryOptions({
    queryKey: ['inventoree', 'discovered', 'duplicates', o],
    queryFn: () => apiGet<DiscoveredDuplicates>(`${ROW}/duplicates?${consolidateQuery(o)}`),
    staleTime: 0,
  })
}

/**
 * Aplica grupurile alese (supravietuitorul + randurile de sters). Serverul
 * reface planul si sare tot ce nu mai e valid (`skipped`).
 */
export function consolidateDiscoveredDuplicates(
  o: ConsolidateOptions,
  groups: { survivor: number; remove: number[] }[],
  stale: number[],
) {
  return apiPostJson<DiscoveredDuplicates>(`${ROW}/duplicates`, { ...o, groups, stale })
}

/** Curatenia pe vechime, separata de unirea dublurilor. */
export interface OldRowsOptions {
  days: number
  /** 0 = toate sediile */
  office: number
}

export interface DiscoveredOldRows extends DiscoveredStatus {
  days: number
  office: number
  /** Cele mai vechi intai; cele cu `referenced` nu se sterg. */
  rows: DuplicateRow[]
  deletable: number
  linked: number
  offices?: { id: number; name: string }[]
  can_apply?: boolean
}

export function discoveredOldRowsQuery(o: OldRowsOptions) {
  return queryOptions({
    queryKey: ['inventoree', 'discovered', 'old-rows', o],
    queryFn: () => apiGet<DiscoveredOldRows>(`${ROW}/old-rows?${new URLSearchParams({ days: String(o.days), office: String(o.office) }).toString()}`),
    staleTime: 0,
  })
}

/** Sterge randurile vechi bifate; serverul reface lista si sare ce nu mai e valid (`skipped`). */
export function deleteDiscoveredOldRows(o: OldRowsOptions, ids: number[]) {
  return apiPostJson<DiscoveredStatus & { removed?: number; skipped?: number; days?: number }>(`${ROW}/old-rows`, { ...o, ids })
}

export function setDiscoveredAutoConsolidate(enabled: boolean) {
  return apiPost<DiscoveredStatus & { auto?: boolean }>(`${ROW}/duplicates/auto`, { enabled: enabled ? 1 : 0 })
}

export function clearDiscovered() {
  return apiPost<DiscoveredStatus & { removed: number }>(`${ROW}/clear`, {})
}

export function clearRelayDiscovered(relayId: number) {
  return apiPost<DiscoveredStatus & { removed?: number }>(`${ROW}/relay/${relayId}/clear`, {})
}

export function identifyRelayDiscovered(relayId: number) {
  return apiPost<DiscoveredStatus & { updated?: number }>(`${ROW}/relay/${relayId}/identify`, {})
}

export const discoveredEventsQuery = queryOptions({
  queryKey: ['inventoree', 'discovered', 'events'],
  queryFn: () => apiGet<{ events: DiscoveredEvent[] }>(`${ROW}/events`),
  staleTime: 30 * 1000,
})
